import React, { useState, useEffect } from "react";
import "./smartEIA.css";
import API_Root from "../../Constants/API_Root";
import { auth } from "../../Authentication/firebase/firebase";
import { authFetch } from "../../Authentication/authService";

const SmartEIA = () => {
  const [isLogged, setIslogged] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState([]);

  // 🔹 AUTHORIZED EMAILS
  const allowedEmails = ["dilipt@capitalnumbers.com", "ildiko@essolutions.ai"];

  // 🔹 CHECK ACCESS
  const isAuthorizedUser = allowedEmails.includes(userEmail?.toLowerCase());

  useEffect(() => {
    const isLoggedIn = localStorage.getItem("isLoggedIn") === "true";

    setIslogged(isLoggedIn);

    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setIslogged(true);
        setUserEmail(user.email || "");
      } else {
        setIslogged(false);
        setUserEmail("");
      }
    });

    return () => unsubscribe();
  }, []);

  const [formData, setFormData] = useState({
    project_name: "",
    industry: "",
    project_phase: "",
    location: "",
    existing_env_data: "",
    sensitive_receptors: "",
    funding_source: "",
  });

  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [successMessage, setSuccessMessage] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (e) => {
    setUploadedFiles(Array.from(e.target.files));
  };

  const startFakeProgress = () => {
    let value = 0;

    const interval = setInterval(() => {
      value += Math.floor(Math.random() * 12);

      if (value >= 92) {
        value = 92;
        clearInterval(interval);
      }

      setProgress(value);
    }, 500);

    return interval;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // 🔹 BLOCK UNAUTHORIZED USERS
    if (!isAuthorizedUser) {
      return;
    }

    setLoading(true);
    setSuccessMessage("");
    setProgress(0);

    const progressInterval = startFakeProgress();

    try {
      const payload = new FormData();

      // Add all text fields
      Object.entries(formData).forEach(([key, value]) => {
        payload.append(key, value);
      });

      // Add files
      uploadedFiles.forEach((file) => {
        payload.append("files", file); // or "file" depending on backend
      });

      const response = await authFetch(`${API_Root}eia/eia-chat`, {
        method: "POST",
        body: payload,
      });

      if (!response.ok) {
        throw new Error("Failed to generate report");
      }

      const blob = await response.blob();

      clearInterval(progressInterval);
      setProgress(100);

      const fileURL = window.URL.createObjectURL(blob);

      const link = document.createElement("a");

      link.href = fileURL;

      link.download = `${formData.project_name || "SmartEIA_Report"}.doc`;

      document.body.appendChild(link);

      link.click();

      link.remove();

      setSuccessMessage(
        "Your Smart EIA report has been generated successfully."
      );

      // 🔹 RESET FORM
      setFormData({
        project_name: "",
        industry: "",
        project_phase: "",
        location: "",
        existing_env_data: "",
        sensitive_receptors: "",
        funding_source: "",
      });
      setUploadedFiles([]);
    } catch (error) {
      console.error(error);

      alert("Something went wrong while generating the report.");
    } finally {
      setTimeout(() => {
        setLoading(false);
        setProgress(0);
      }, 1200);
    }
  };

  return (
    <div className="smartEia-main-wrapper">
      {/* Background Glow */}
      <div className="smartEia-bg-glow smartEia-bg-glow-one"></div>
      <div className="smartEia-bg-glow smartEia-bg-glow-two"></div>

      <div className="smartEia-container">
        {/* LEFT PANEL */}
        <div className="smartEia-left-panel">
          <div className="smartEia-badge">E&S Powered EIA Generator</div>

          <h1 className="smartEia-title">
            Accelerate Your Environmental Permitting
          </h1>

          <p className="smartEia-description">
            Move from project concept to an 80%-ready National EIA Scoping
            Report in minutes. smartEIA is an intelligent drafting engine
            designed for project developers and E&S practitioners.
          </p>

          {/* Highlight Card */}
          <div className="smartEia-highlight-card">
            <h3>⚡ 80% Ready Output</h3>

            <p>
              Generate highly structured EIA Scoping Reports aligned with the EU
              EIA Directive template while eliminating weeks of preliminary desk
              research.
            </p>
          </div>

          {/* HOW IT WORKS */}
          <div className="smartEia-section-block">
            <h3>How It Works</h3>

            <div className="smartEia-step-card">
              <span>1</span>

              <div>
                <h4>Provide Your Project Data</h4>

                <p>
                  Enter your industry, phase, location coordinates,
                  environmental baseline data, and nearby sensitive receptors.
                </p>
              </div>
            </div>

            <div className="smartEia-step-card">
              <span>2</span>

              <div>
                <h4>Location-Aware Research</h4>

                <p>
                  smartEIA identifies national legislation, ecological habitats,
                  and regional proxy data relevant to your project area.
                </p>
              </div>
            </div>

            <div className="smartEia-step-card">
              <span>3</span>

              <div>
                <h4>Generate the Report</h4>

                <p>
                  Download a comprehensive EU-compliant EIA Scoping Report in
                  DOC format tailored to your project.
                </p>
              </div>
            </div>
          </div>

          {/* FEATURES */}
          <div className="smartEia-section-block">
            <h3>Key Features</h3>

            <div className="smartEia-feature-list">
              <div className="smartEia-feature-card">
                <span>📘</span>

                <div>
                  <h4>Built to EU Standards</h4>

                  <p>
                    Structured to comply with the EU EIA Directive (2011/92/EU
                    amended by 2014/52/EU).
                  </p>
                </div>
              </div>

              <div className="smartEia-feature-card">
                <span>🧠</span>

                <div>
                  <h4>Targeted Gap Analysis</h4>

                  <p>
                    smartEIA clearly flags missing primary data with
                    <strong> [HUMAN ACTION REQUIRED]</strong> markers.
                  </p>
                </div>
              </div>

              <div className="smartEia-feature-card">
                <span>📄</span>

                <div>
                  <h4>Seamless Export</h4>

                  <p>
                    Download reports instantly in Word format and archive them
                    within your workflow.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT FORM */}
        <div className="smartEia-form-card">
          <div className="smartEia-form-header">
            <h2>Create Smart EIA</h2>

            <p>Fill in the project details to generate your report.</p>
          </div>

          {/* 🔹 SHOW CURRENT USER EMAIL */}
          <div className="smartEia-user-email">
            Logged in as: {userEmail || "No User"}
          </div>

          <form onSubmit={handleSubmit} className="smartEia-form">
            <div className="smartEia-input-group">
              <label>Project Name</label>

              <input
                type="text"
                name="project_name"
                placeholder="Enter project name"
                value={formData.project_name}
                onChange={handleChange}
                required
                disabled={!isAuthorizedUser}
              />
            </div>

            <div className="smartEia-input-group">
              <label>Industry</label>

              <input
                type="text"
                name="industry"
                placeholder="Example: Renewables"
                value={formData.industry}
                onChange={handleChange}
                required
                disabled={!isAuthorizedUser}
              />
            </div>

            <div className="smartEia-grid-two">
              <div className="smartEia-input-group">
                <label>Project Phase</label>

                <select
                  name="project_phase"
                  value={formData.project_phase}
                  onChange={handleChange}
                  required
                  disabled={!isAuthorizedUser}
                >
                  <option value="">Select Phase</option>

                  <option value="Scoping">Scoping</option>

                  <option value="Planning">Planning</option>

                  <option value="Execution">Execution</option>

                  <option value="Screening">Screening</option>
                </select>
              </div>

              <div className="smartEia-input-group">
                <label>Funding Source</label>

                <input
                  type="text"
                  name="funding_source"
                  placeholder="Example: EBRD"
                  value={formData.funding_source}
                  onChange={handleChange}
                  required
                  disabled={!isAuthorizedUser}
                />
              </div>
            </div>

            <div className="smartEia-input-group">
              <label>Supporting Documents (Optional)</label>

              <input
                type="file"
                multiple
                onChange={handleFileChange}
                disabled={!isAuthorizedUser}
              />

              {uploadedFiles.length > 0 && (
                <div className="smartEia-selected-files">
                  {uploadedFiles.map((file, index) => (
                    <div key={index}>{file.name}</div>
                  ))}
                </div>
              )}
            </div>

            <div className="smartEia-input-group">
              <label>Project Location</label>

              <textarea
                rows="2"
                name="location"
                placeholder="Enter project coordinates or location"
                value={formData.location}
                onChange={handleChange}
                required
                disabled={!isAuthorizedUser}
              ></textarea>
            </div>

            <div className="smartEia-input-group">
              <label>Existing Environmental Data</label>

              <textarea
                rows="2"
                name="existing_env_data"
                placeholder="Enter available environmental data"
                value={formData.existing_env_data}
                onChange={handleChange}
                required
                disabled={!isAuthorizedUser}
              ></textarea>
            </div>

            <div className="smartEia-input-group">
              <label>Sensitive Receptors</label>

              <textarea
                rows="3"
                name="sensitive_receptors"
                placeholder="Mention nearby habitats, schools, protected zones etc."
                value={formData.sensitive_receptors}
                onChange={handleChange}
                required
                disabled={!isAuthorizedUser}
              ></textarea>
            </div>

            {!loading ? (
              <button
                type="submit"
                className={`smartEia-submit-btn ${
                  !isAuthorizedUser ? "smartEia-submit-btn-disabled" : ""
                }`}
                disabled={!isAuthorizedUser}
              >
                <span>
                  {isAuthorizedUser
                    ? "Generate Smart EIA Report"
                    : "Access Restricted"}
                </span>
              </button>
            ) : (
              <div className="smartEia-loading-wrapper">
                <div className="smartEia-loader-ring"></div>

                <h4>Generating Smart EIA...</h4>

                <p>
                  AI is analyzing project data and preparing your environmental
                  report.
                </p>

                <div className="smartEia-progress-container">
                  <div
                    className="smartEia-progress-bar"
                    style={{
                      width: `${progress}%`,
                    }}
                  ></div>
                </div>

                <span className="smartEia-progress-text">
                  {progress}% Completed
                </span>
              </div>
            )}

            {!isAuthorizedUser && (
              <div className="smartEia-access-note">
                This SmartEIA beta is currently restricted to approved internal
                users.
              </div>
            )}

            {successMessage && (
              <div className="smartEia-success-message">
                ✅ {successMessage}
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default SmartEIA;
